import axiosCreator from "axios";
import { toast } from "react-toastify";

export let axios = axiosCreator.create({
  baseURL: process.env.REACT_APP_URL ?? "http://localhost:1337",
});
// Send cookies (session) along with requests to backend
axios.defaults.withCredentials = true;
let navigator = null;
export const setNavigator = (_navigator) => {
  navigator = _navigator;
};
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log("Error occurred in apiClient.js", error);
    const data = error.response.data;
    if (error.response.status === 401) {
      localStorage.removeItem("token");
      if (navigator) navigator("/admin/login", { replace: true });
    }
    if (data.type === "info") toast.info(`${data.message}`);
    else toast.error(`${data.message}`);
    return Promise.reject(error);
  },
);

class APIFactory {
  static getModel(url) {
    return async ({ page, limit, query, sortOptions, filters, ...params }) => {
      if (page < 1) page = 1;

      const res = await axios.get(url, {
        params: { page, limit, query, sortOptions, filters, ...params },
      });
      return res.data;
    };
  }

  static postToModel(url) {
    return async (data) => {
      const res = await axios.post(url, data);
      return res.data;
    };
  }
}

const api = {
  //AUTH
  login: APIFactory.postToModel("/login"),
  // USER FUNCTIONS
  getUsers: APIFactory.getModel("/admin/users/all"),
  getCommunityAdminUsers: APIFactory.getModel("/community-admin/users"),
  getCommunityAdminCommunities: APIFactory.getModel(
    "/community-admin/communities",
  ),
  deleteUsers: APIFactory.postToModel("/admin/users/delete"),
  disableUsers: APIFactory.postToModel("/admin/users/disable"),
  reinstateUsers: APIFactory.postToModel("/admin/users/reinstate"),
  // COMMUNITY FUNCTIONS
  getCommunities: APIFactory.getModel("/admin/communities/all"),
  getCommunityUsers: APIFactory.getModel("/admin/community/paginated-users"),
  addCommunity: APIFactory.postToModel("/admin/communities"),
  updateCommunity: APIFactory.postToModel("/admin/communities/update"),
  deleteCommunities: APIFactory.postToModel("/admin/communities/delete"),
  disableCommunities: APIFactory.postToModel("/admin/communities/disable"),
  reinstateCommunities: APIFactory.postToModel("/admin/communities/reinstate"),
  assignAdminToCommunity: APIFactory.postToModel(
    "/admin/communities/assign-admin",
  ),
  // inviteUserToCommunity: APIFactory.postToModel("/admin/communities/invite-user"),
  inviteUserToCommunity: async (params) => {
    const isCommunityAdminRoute =
      typeof window !== "undefined" &&
      window.location.pathname?.startsWith("/community-admin");
    if (isCommunityAdminRoute) {
      const res = await axios.post(
        "/community-admin/communities/invite-user",
        params,
      );
      return res.data;
    }
    return APIFactory.postToModel("/admin/communities/invite-user")(params);
  },
  removeMemberFromCommunity: APIFactory.postToModel(
    "/admin/communities/remove-member",
  ),
  checkCommunityMember: async ({ userId, communityId }) => {
    const res = await axios.post("/admin/communities/check-member", {
      userId,
      communityId,
    });
    return res.data;
  },
  getCommunityDetail: async (id) => {
    const res = await axios.get(`/admin/communities/${id}`);
    return res.data;
  },
  getCommunityEventsCount: async (communityId) => {
    const res = await axios.get(`/community/events-count`, {
      params: { communityId },
    });
    return res.data;
  },
  // GROUP FUNCTIONS
  getGroups: APIFactory.getModel("/admin/groups/all"),

  getGroupEventsCount: async (groupId) => {
    const res = await axios.get(`/community/events-count`, {
      params: { groupId },
    });
    return res.data;
  },
  // Get single group (public route returns group with members and pagination info)
  getGroup: async (groupId) => {
    const res = await axios.get(`/get/group/${groupId}`);
    return res.data?.group || null;
  },

  // Get paginated group members for admin table views. Maps public route response to useTable shape.
  // Accepts defaultParams from useTable: { page, limit, query, sortOptions, filters }
  getGroupMembers: async (groupId, params = {}) => {
    const { page = 1, ...rest } = params || {};
    const res = await axios.get(`/get/group/${groupId}/${page}`, {
      params: { ...rest },
    });
    const payload = res.data || {};
    return {
      data: (payload.group && payload.group.members) || [],
      count: payload.totalMembers || 0,
      totalPages: payload.totalPages || 1,
    };
  },

  getCommunityAdminGroups: APIFactory.getModel("/community-admin/groups"),
  deleteGroups: APIFactory.postToModel("/admin/groups/delete"),
  disableGroups: APIFactory.postToModel("/admin/groups/disable"),
  reinstateGroups: APIFactory.postToModel("/admin/groups/reinstate"),
  // removeMemberFromGroup: (body) =>
  //   APIFactory.postToModel("/group/remove/member", body),
  removeMemberFromGroup: async (params) => {
    const res = await axios.post("/group/remove/member", { ...params });
    return res.data;
  },
  // REPORT FUNCTIONS
  getPostReports: async (params) => {
    const isCommunityAdminRoute =
      typeof window !== "undefined" &&
      window.location.pathname?.startsWith("/community-admin");
    if (isCommunityAdminRoute) {
      const res = await axios.get("/community-admin/reports", {
        params: { ...params, type: "post" },
      });
      return res.data;
    }
    return APIFactory.getModel("/admin/reports/post")(params);
  },
  getGroupReports: async (params) => {
    const isCommunityAdminRoute =
      typeof window !== "undefined" &&
      window.location.pathname?.startsWith("/community-admin");
    if (isCommunityAdminRoute) {
      const res = await axios.get("/community-admin/reports", {
        params: { ...params, type: "group" },
      });
      return res.data;
    }
    return APIFactory.getModel("/admin/reports/group")(params);
  },
  getUserReports: async (params) => {
    const isCommunityAdminRoute =
      typeof window !== "undefined" &&
      window.location.pathname?.startsWith("/community-admin");
    if (isCommunityAdminRoute) {
      const res = await axios.get("/community-admin/reports", {
        params: { ...params, type: "user" },
      });
      return res.data;
    }
    return APIFactory.getModel("/admin/reports/user")(params);
  },
  getCommentReports: async (params) => {
    const isCommunityAdminRoute =
      typeof window !== "undefined" &&
      window.location.pathname?.startsWith("/community-admin");
    if (isCommunityAdminRoute) {
      const res = await axios.get("/community-admin/reports", {
        params: { ...params, type: "comment" },
      });
      return res.data;
    }
    return APIFactory.getModel("/admin/reports/comment")(params);
  },
  getMessageReports: async (params) => {
    const isCommunityAdminRoute =
      typeof window !== "undefined" &&
      window.location.pathname?.startsWith("/community-admin");
    if (isCommunityAdminRoute) {
      const res = await axios.get("/community-admin/reports", {
        params: { ...params, type: "message" },
      });
      return res.data;
    }
    return APIFactory.getModel("/admin/reports/message")(params);
  },
  resolveReports: APIFactory.postToModel("/admin/reports/resolve"),
  sendWarning: APIFactory.postToModel("/admin/reports/warn"),
  // POST FUNCTIONS
  deletePosts: APIFactory.postToModel("/admin/posts/delete"),

  // COMMENT FUNCTIONS
  deleteComments: APIFactory.postToModel("/admin/comments/delete"),

  // FEEBACK FUNCTIONS
  getFeedbacks: async (params) => {
    const isCommunityAdminRoute =
      typeof window !== "undefined" &&
      window.location.pathname?.startsWith("/community-admin");
    if (isCommunityAdminRoute) {
      const res = await axios.get("/community-admin/feedbacks", { params });
      return res.data;
    }
    return APIFactory.getModel("/admin/feedbacks/all")(params);
  },
  // getFeedbacks: APIFactory.getModel("/admin/feedbacks/all"),
  resolveFeedbacks: APIFactory.postToModel("/admin/feedbacks/resolve"),
  respondToFeedback: APIFactory.postToModel("/admin/feedbacks/respond"),

  // TAG FUNCTIONS
  updateTags: APIFactory.postToModel("/admin/tags/update"),
  getTagsByCommunity: async (communityId) => {
    const res = await axios.get(`/admin/tags?communityId=${communityId}`);
    return res.data;
  },

  // LOCATION FUNCTIONS
  getBrowserLocation: async () => {
    const res = await axios.get("/location/get-browser-location");
    return res.data;
  },
  saveGPSLocation: async (lat, lng, accuracy) => {
    const res = await axios.post("/location/save-gps", {
      lat,
      lng,
      accuracy,
    });
    return res.data;
  },
};
export default api;
